/**
 * Adaptive Today widgets — iter111.
 *
 * The Today grid is a single SortableGrid whose fixed row height (see
 * PatientHome) gives every span its correct aspect:
 *
 *     1×1 ≈ 304×200 → sm  (compact)
 *     2×1 ≈ 628×200 → wide
 *     1×2 ≈ 304×416 → tall
 *     2×2 ≈ 628×416 → lg  (large)
 *
 * `bucketFor` measures the actual border-box so the same widget adapts on a
 * 390 px viewport where a 2×2 label ends up ~355×416 → still `lg`, but a
 * 2×1 that wraps onto one column falls back to `sm` gracefully.
 *
 * Rules the widgets obey (do not remove without approval):
 *   • Every widget owns a header row (function name + status) at the top.
 *   • No card centres a single giant number in the middle. `justify-between`
 *     only for `sm` where the number IS the content.
 *   • Resize reveals new content without a click/refresh — SWR cache is shared
 *     between sizes; only render selection changes.
 *   • All fetches are cached with SWR (`revalidateOnFocus:false`,
 *     `dedupingInterval:30s`). No per-card polling. Unread summary allows
 *     focus revalidation only.
 *   • Every navigational anchor / button honours the global `editing` flag —
 *     clicks, Enter and form submits are all guarded.
 *
 * Public API — unchanged callers only see `<AdaptiveWidget kind={id}>`.
 * Legacy `qa_*` ids from saved layouts resolve to the same kinds via the
 * `LEGACY_KIND_ALIAS` map in PatientHome.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import useSWR from "swr";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { toast } from "sonner";
import {
  MessageSquare, Activity, Dumbbell, CalendarPlus, FileText, Video,
  BookOpen, MessagesSquare, CreditCard, Watch, ShieldCheck, Sparkles,
  ClipboardList, TrendingUp, Bell, ArrowRight, Send, Target, Award,
  Flame, Play, ChevronRight, Gift, CheckCircle2,
} from "lucide-react";
import api from "@/lib/api";
import { fetchMergedWellness } from "@/lib/wellnessAdapter";
import { WELLNESS_PALETTE } from "@/lib/metricColors";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { useEditMode } from "@/lib/editMode";

/* ─── Hooks ───────────────────────────────────────────────────────── */
function useTileSize(ref) {
  const [size, setSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return;
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.round(r.width), h: Math.round(r.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return size;
}

// Four buckets from ACTUAL rendered dimensions.
//   wide  ⇔ w >= 460   (2 cols on desktop; 1 col on mobile stays narrow)
//   tall  ⇔ h >= 300   (row height 200 + gap 16 + row 200 = 416)
function bucketFor(w, h) {
  const wide = w >= 460;
  const tall = h >= 300;
  if (wide && tall) return "2x2";
  if (wide && !tall) return "2x1";
  if (!wide && tall) return "1x2";
  return "1x1";
}

const KIND_META = {
  // kind → label + primary route + icon.
  messages:      { icon: MessageSquare,   label: "Messages",           to: "/app/messages" },
  msg_clinician: { icon: MessageSquare,   label: "Message my clinician", to: "/app/messages" },
  wellness:      { icon: Activity,        label: "Physical Wellness",  to: "/app/progress?section=wellness" },
  plan:          { icon: Dumbbell,        label: "Today's Plan",       to: "/app/plan" },
  appointments:  { icon: CalendarPlus,    label: "Appointments",       to: "/app/appointments" },
  booking:       { icon: CalendarPlus,    label: "Booking",            to: "/app/appointments" },
  forms:         { icon: FileText,        label: "Forms",              to: "/app/forms" },
  videos:        { icon: Video,           label: "Videos",             to: "/app/videos" },
  progress:      { icon: TrendingUp,      label: "Progress",           to: "/app/progress" },
  adherence:     { icon: Flame,           label: "Adherence · 7d",     to: "/app/progress" },
  tests:         { icon: ClipboardList,   label: "Physical Tests",     to: "/app/progress?section=tests" },
  notifications: { icon: Bell,            label: "Notifications",      to: "/app/messages" },
  learn:         { icon: BookOpen,        label: "Learn",              to: "/app/learn" },
  community:     { icon: MessagesSquare,  label: "Community",          to: "/app/community" },
  membership:    { icon: CreditCard,      label: "Membership",         to: "/app/packages" },
  wearables:     { icon: Watch,           label: "Wearables",          to: "/app/wearables" },
  security:      { icon: ShieldCheck,     label: "Security",           to: "/security-settings" },
  checkin:       { icon: Sparkles,        label: "Check-In",           to: "/app/coach" },
  goal:          { icon: Target,          label: "My goal",            to: "/app/plan" },
  rewards:       { icon: Award,           label: "My rewards",         to: "/app/packages" },
};

/* ─── Shared shell ─────────────────────────────────────────────────── */
function Shell({ kind, testid, statusRight, children }) {
  const ref = useRef(null);
  const size = useTileSize(ref);
  const b = bucketFor(size.w, size.h);
  const meta = KIND_META[kind] || { icon: Sparkles, label: kind, to: "/app" };
  const Icon = meta.icon;
  const { editing } = useEditMode();
  return (
    <Card
      ref={ref}
      className="fl-card fl-card-elevated h-full flex flex-col p-4 border-white/10 bg-transparent overflow-hidden"
      data-testid={testid}
      data-widget-kind={kind}
      data-widget-bucket={b}
    >
      <CardHeader className="p-0 pb-2 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className="text-[#f47a20] shrink-0" size={16} />
          <CardDescription className="fl-label text-[10px] text-zinc-400 truncate flex-1">
            {meta.label}
          </CardDescription>
          {statusRight ? (
            <span className="text-[10px] text-zinc-500 shrink-0">{statusRight}</span>
          ) : null}
          {b !== "1x1" && (
            <Link
              to={meta.to}
              className="text-[10px] uppercase tracking-widest text-[#f47a20] hover:underline shrink-0 inline-flex items-center gap-0.5"
              tabIndex={editing ? -1 : 0}
              aria-disabled={editing}
              onClick={(e) => { if (editing) e.preventDefault(); }}
              data-testid={`${testid}-open-full`}
            >
              Open <ChevronRight size={10} />
            </Link>
          )}
        </div>
      </CardHeader>
      <CardContent
        className="p-0 flex-1 min-h-0 flex flex-col"
        style={editing ? { pointerEvents: "none", opacity: 0.85 } : undefined}
      >
        {typeof children === "function" ? children({ bucket: b, size, meta, editing }) : children}
      </CardContent>
    </Card>
  );
}

/* Small navigational button used in the footer of most widgets. */
function CardCTA({ to, label, editing, testid, primary = false, disabled = false }) {
  const cls = primary
    ? "fl-btn h-8 text-xs px-3 w-full justify-center inline-flex items-center gap-1"
    : "text-xs text-[#f47a20] hover:underline inline-flex items-center gap-1";
  return (
    <Link
      to={to}
      className={cls + (disabled ? " opacity-50 pointer-events-none" : "")}
      tabIndex={editing ? -1 : 0}
      aria-disabled={editing || disabled}
      onClick={(e) => { if (editing || disabled) e.preventDefault(); }}
      data-testid={testid}
    >
      {label} <ArrowRight size={12} />
    </Link>
  );
}

const swrOpts = { revalidateOnFocus: false, dedupingInterval: 30_000 };
const unreadOpts = { revalidateOnFocus: true, dedupingInterval: 5_000 };

/* ─── Messages ──────────────────────────────────────────────────── */
export function MessagesWidget({ testid = "widget-messages" }) {
  return (
    <Shell kind="messages" testid={testid}>
      {({ bucket, editing }) => <MessagesBody bucket={bucket} editing={editing} />}
    </Shell>
  );
}
function MessagesBody({ bucket, editing }) {
  const { data: unreadSummary, mutate: mutateUnread } = useSWR(
    "/messages/unread-summary", (u) => api.get(u).then((r) => r.data), unreadOpts,
  );
  const total = unreadSummary?.total ?? 0;
  const senders = unreadSummary?.per_sender || unreadSummary?.senders || {};
  const primaryId = useMemo(() => {
    const entries = Object.entries(senders);
    if (!entries.length) return null;
    entries.sort((a, b) => {
      const av = typeof a[1] === "number" ? a[1] : (a[1]?.count ?? 0);
      const bv = typeof b[1] === "number" ? b[1] : (b[1]?.count ?? 0);
      return bv - av;
    });
    return entries[0][0];
  }, [senders]);

  const { data: contacts } = useSWR(
    bucket !== "1x1" ? "/messages/contacts" : null,
    (u) => api.get(u).then((r) => r.data), swrOpts,
  );
  const contactList = Array.isArray(contacts) ? contacts : [];
  const [selected, setSelected] = useState(null);
  const active = selected || primaryId || contactList[0]?.id || null;
  const activeContact = contactList.find((c) => c.id === active) || null;
  const previewName = activeContact?.name || activeContact?.other_name || senders?.[active]?.name || "your clinician";

  const { data: thread, mutate: refreshThread } = useSWR(
    (bucket === "2x2" || bucket === "1x2") && active ? `/messages/thread/${active}?limit=10` : null,
    (u) => api.get(u).then((r) => r.data), swrOpts,
  );
  const items = thread?.messages || [];

  const [drafts, setDrafts] = useState({});
  const draft = active ? (drafts[active] || "") : "";
  const setDraft = (v) => setDrafts((prev) => ({ ...prev, [active]: v }));
  const [sending, setSending] = useState(false);
  const onSend = async () => {
    if (editing) return;
    const body = draft.trim();
    if (!body || sending || !active) return;
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: active, body });
      setDrafts((prev) => { const n = { ...prev }; delete n[active]; return n; });
      refreshThread();
      mutateUnread();
      toast.success("Message sent");
    } catch {
      toast.error("Send failed — draft kept. Try again.");
    } finally {
      setSending(false);
    }
  };

  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-heading text-3xl text-[#f47a20] leading-none" data-testid="widget-messages-count">{total}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">unread</div>
        <CardCTA to="/app/messages" label="Open Messages" editing={editing} testid="widget-messages-open-sm" />
      </div>
    );
  }
  if (bucket === "2x1") {
    const latest = items[items.length - 1] || null;
    return (
      <div className="flex-1 flex flex-col text-xs gap-2 min-h-0">
        <div className="text-zinc-300 truncate"><span className="text-zinc-500">Reply to</span> {previewName}</div>
        {latest ? (
          <div className="text-zinc-400 line-clamp-2">{latest.body}</div>
        ) : (
          <div className="text-zinc-500">{total > 0 ? `${total} unread — open to read.` : "No new messages."}</div>
        )}
        <div className="mt-auto flex gap-2 items-end">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") onSend(); }}
            placeholder={`Message ${previewName}`}
            className="fl-input text-xs flex-1 h-8"
            data-testid="widget-messages-composer-inline"
            disabled={editing}
          />
          <button type="button" onClick={onSend} disabled={sending || !draft.trim() || editing}
                  className="fl-btn text-xs h-8 px-2 disabled:opacity-50" data-testid="widget-messages-send-inline">
            <Send size={12} />
          </button>
        </div>
      </div>
    );
  }
  // 1x2 and 2x2 — full composer
  return (
    <div className="flex-1 flex flex-col min-h-0">
      {contactList.length > 1 && (
        <select value={active || ""} onChange={(e) => setSelected(e.target.value)}
                className="fl-input text-xs mb-2" data-testid="widget-messages-recipient" disabled={editing}>
          {contactList.map((c) => <option key={c.id} value={c.id}>{c.name || c.other_name || c.id}</option>)}
        </select>
      )}
      {!active ? (
        <div className="text-zinc-500 text-xs flex-1 flex items-center justify-center">No conversations yet.</div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-1.5" data-testid="widget-messages-list">
          {items.length === 0 && <div className="text-zinc-500 text-xs italic">No messages in this thread yet.</div>}
          {items.map((m) => (
            <div key={m.id} className={`text-xs ${m.own ? "text-right" : ""}`}>
              <span className={`inline-block px-2 py-1 rounded-md ${m.own ? "bg-[#f47a20]/25 text-white" : "bg-white/5 text-zinc-200"}`}>
                {m.body}
              </span>
            </div>
          ))}
        </div>
      )}
      {active && (
        <div className="mt-2 flex gap-2 items-end shrink-0">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
                    rows={2} placeholder={`Message ${previewName}`}
                    className="fl-input text-xs flex-1 resize-none" data-testid="widget-messages-composer"
                    disabled={editing} />
          <button type="button" onClick={onSend} disabled={sending || !draft.trim() || editing}
                  className="fl-btn text-xs h-8 px-3 inline-flex items-center gap-1 disabled:opacity-50" data-testid="widget-messages-send">
            <Send size={12} /> {sending ? "…" : "Send"}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Message my clinician — dedicated shortcut, shares Messages endpoint but
   always targets the patient's first contact (the assigned clinician). */
export function MsgClinicianWidget({ testid = "widget-msg-clinician" }) {
  return (
    <Shell kind="msg_clinician" testid={testid}>
      {({ bucket, editing }) => <MsgClinicianBody bucket={bucket} editing={editing} />}
    </Shell>
  );
}
function MsgClinicianBody({ bucket, editing }) {
  const { data: contacts } = useSWR("/messages/contacts", (u) => api.get(u).then((r) => r.data), swrOpts);
  const clinician = (Array.isArray(contacts) ? contacts : []).find((c) => c.role === "clinician") ||
                     (Array.isArray(contacts) ? contacts[0] : null) || null;
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const { data: thread, mutate } = useSWR(
    (bucket === "2x2" || bucket === "1x2") && clinician?.id ? `/messages/thread/${clinician.id}?limit=6` : null,
    (u) => api.get(u).then((r) => r.data), swrOpts,
  );
  const send = async () => {
    if (editing || !clinician?.id || !draft.trim()) return;
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: clinician.id, body: draft.trim() });
      setDraft("");
      mutate();
      toast.success(`Sent to ${clinician.name || "your clinician"}`);
    } catch {
      toast.error("Send failed — draft kept.");
    } finally { setSending(false); }
  };

  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-sm text-zinc-300 line-clamp-2">Message {clinician?.name || "your clinician"}</div>
        <CardCTA to="/app/messages" label="Open" editing={editing} testid="widget-msg-clinician-open-sm" />
      </div>
    );
  }
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs gap-2 min-h-0">
        <div className="text-zinc-400 text-xs">To <span className="text-white">{clinician?.name || "your clinician"}</span></div>
        <div className="mt-auto flex gap-2 items-end">
          <input value={draft} onChange={(e) => setDraft(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") send(); }}
                 placeholder="Ask a question or share how you feel"
                 className="fl-input text-xs flex-1 h-8" data-testid="widget-msg-clinician-inline" disabled={editing} />
          <button type="button" onClick={send} disabled={sending || !draft.trim() || editing || !clinician?.id}
                  className="fl-btn text-xs h-8 px-2 disabled:opacity-50" data-testid="widget-msg-clinician-send-inline">
            <Send size={12} />
          </button>
        </div>
      </div>
    );
  }
  const items = thread?.messages || [];
  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="text-xs text-zinc-400 mb-2">To <span className="text-white">{clinician?.name || "your clinician"}</span></div>
      <div className="flex-1 min-h-0 overflow-y-auto space-y-1.5 pr-1">
        {items.length === 0 && <div className="text-zinc-500 text-xs italic">Start a conversation.</div>}
        {items.map((m) => (
          <div key={m.id} className={`text-xs ${m.own ? "text-right" : ""}`}>
            <span className={`inline-block px-2 py-1 rounded-md ${m.own ? "bg-[#f47a20]/25 text-white" : "bg-white/5 text-zinc-200"}`}>{m.body}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 flex gap-2 items-end shrink-0">
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={2} placeholder="Message my clinician"
                  className="fl-input text-xs flex-1 resize-none" data-testid="widget-msg-clinician-composer"
                  disabled={editing} />
        <button type="button" onClick={send} disabled={sending || !draft.trim() || editing || !clinician?.id}
                className="fl-btn text-xs h-8 px-3 inline-flex items-center gap-1 disabled:opacity-50" data-testid="widget-msg-clinician-send">
          <Send size={12} /> Send
        </button>
      </div>
    </div>
  );
}

/* ─── Physical Wellness ───────────────────────────────────────────── */
export function PhysicalWellnessWidget({ testid = "widget-wellness" }) {
  return <Shell kind="wellness" testid={testid}>{({ bucket }) => <WellnessBody bucket={bucket} />}</Shell>;
}
function WellnessBody({ bucket }) {
  const { data } = useSWR("wellness:merged", () => fetchMergedWellness(), swrOpts);
  const merged = Array.isArray(data) ? data : [];
  const series = useMemo(() =>
    merged.slice(-14).map((c) => ({
      _ts: new Date(c.date + "T00:00:00").getTime(),
      pain: c.pain, fatigue: c.fatigue, confidence: c.confidence, sleep_quality: c.sleep_quality,
    })), [merged]);
  const latest = merged[merged.length - 1] || null;
  const fmtDate = (ts) => new Date(ts).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });

  if (bucket === "1x1") {
    return latest ? (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-label text-[10px] text-zinc-500">Pain</div>
        <div className="fl-heading text-3xl text-[#f47a20] leading-none">
          {latest.pain ?? "—"}<span className="text-base text-zinc-500">/10</span>
        </div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{(latest.date || "").slice(0, 10)}</div>
      </div>
    ) : <div className="text-zinc-500 text-xs flex-1 flex items-end">No check-in yet.</div>;
  }
  if (series.length === 0) {
    return <div className="text-zinc-500 text-sm flex-1 flex items-center">Complete a check-in to see your trend.</div>;
  }
  if (bucket === "1x2") {
    const metrics = [
      ["Pain",       latest?.pain, "/10"],
      ["Fatigue",    latest?.fatigue, "/10"],
      ["Sleep qual.", latest?.sleep_quality, "/5"],
      ["Confidence", latest?.confidence, "/10"],
    ];
    return (
      <div className="flex-1 flex flex-col gap-2 min-h-0">
        <div className="grid grid-cols-2 gap-2">
          {metrics.map(([l, v, u]) => (
            <div key={l}>
              <div className="fl-label text-[9px] text-zinc-500">{l}</div>
              <div className="text-lg text-[#f47a20] leading-none">{v ?? "—"}<span className="text-xs text-zinc-500">{u}</span></div>
            </div>
          ))}
        </div>
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
              <XAxis type="number" dataKey="_ts" scale="time" domain={["dataMin", "dataMax"]} stroke="#71717A" fontSize={9} tickFormatter={fmtDate} tickCount={3} />
              <YAxis stroke="#71717A" fontSize={9} domain={[0, 10]} width={20} tickCount={3} />
              <Line type="linear" dataKey="pain" stroke={WELLNESS_PALETTE.pain} strokeWidth={2} dot={false} connectNulls={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  }
  // 2x1 wide chart, 2x2 chart + legend labels
  return (
    <div className="flex-1 min-h-0" data-testid="widget-wellness-chart">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={series} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="var(--fl-border)" strokeDasharray="3 3" opacity={0.4} />
          <XAxis type="number" dataKey="_ts" scale="time" domain={["dataMin", "dataMax"]} stroke="#71717A" fontSize={10} tickFormatter={fmtDate} tickCount={4} tickMargin={4} />
          <YAxis yAxisId="ten"  orientation="left"  stroke="#71717A" fontSize={10} domain={[0, 10]} width={26} tickCount={bucket === "2x1" ? 3 : 6} allowDecimals={false} />
          {bucket === "2x2" && <YAxis yAxisId="five" orientation="right" stroke={WELLNESS_PALETTE.sleep_quality} fontSize={10} domain={[1, 5]} width={22} tickCount={5} allowDecimals={false} />}
          <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} labelStyle={{ color: "#f47a20" }} labelFormatter={(ts) => new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} />
          <Line yAxisId="ten"  type="linear" dataKey="pain"       stroke={WELLNESS_PALETTE.pain}       strokeWidth={2} dot={false} connectNulls={false} name="Pain" />
          {bucket === "2x2" && <Line yAxisId="ten"  type="linear" dataKey="fatigue"       stroke={WELLNESS_PALETTE.fatigue}    strokeWidth={2} dot={false} connectNulls={false} name="Fatigue" />}
          {bucket === "2x2" && <Line yAxisId="ten"  type="linear" dataKey="confidence"    stroke={WELLNESS_PALETTE.confidence} strokeWidth={2} dot={false} connectNulls={false} name="Confidence" />}
          {bucket === "2x2" && <Line yAxisId="five" type="linear" dataKey="sleep_quality" stroke={WELLNESS_PALETTE.sleep_quality} strokeDasharray="5 4" strokeWidth={2} dot={{ r: 3, strokeWidth: 1, fill: "#000" }} connectNulls={false} name="Sleep 1-5" />}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── Physical Tests ─────────────────────────────────────────────── */
export function PhysicalTestsWidget({ testid = "widget-tests" }) {
  return <Shell kind="tests" testid={testid}>{({ bucket }) => <TestsBody bucket={bucket} />}</Shell>;
}
function TestsBody({ bucket }) {
  const { data } = useSWR("/rts-tests/mine", (u) => api.get(u).then((r) => r.data), swrOpts);
  const tests = Array.isArray(data) ? data : [];
  const latest = tests[0] || null;
  if (!latest) return <div className="text-zinc-500 text-xs flex-1 flex items-end">No physical tests recorded yet.</div>;
  const dt = (latest.created_at || latest.date || "").slice(0, 10);
  const results = [
    ["Quad LSI",   latest.quad_lsi,      "%"],
    ["Ham LSI",    latest.ham_lsi,       "%"],
    ["1-Hop LSI",  latest.single_hop_lsi,"%"],
    ["Triple LSI", latest.triple_hop_lsi,"%"],
    ["Crossover LSI", latest.crossover_hop_lsi, "%"],
    ["Timed 6m LSI",  latest.timed_hop_lsi,     "%"],
  ].filter(([, v]) => v != null);

  if (bucket === "1x1") {
    const [label, v, u] = results[0] || ["Status", latest.overall_ready ? "Ready" : "In progress", ""];
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-label text-[10px] text-zinc-500 truncate">{label}</div>
        <div className="text-2xl text-[#f47a20] leading-none">{typeof v === "number" ? `${Math.round(v * 100)}` : v}<span className="text-xs text-zinc-500">{u}</span></div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{dt}</div>
      </div>
    );
  }
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="grid grid-cols-3 gap-2">
          {results.slice(0, 3).map(([l, v, u]) => (
            <div key={l}>
              <div className="fl-label text-[9px] text-zinc-500 truncate">{l}</div>
              <div className="text-[#f47a20]">{Math.round(v * 100)}{u}</div>
            </div>
          ))}
        </div>
        <div className="text-[10px] text-zinc-500 mt-auto">{dt}</div>
      </div>
    );
  }
  // 1x2 & 2x2 — vertical named list with side info
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs">
      {results.map(([l, v, u]) => (
        <div key={l} className="py-1.5 border-b border-white/5 last:border-b-0 flex items-baseline justify-between gap-2">
          <span className="text-zinc-300 truncate">{l}</span>
          <span className="text-[#f47a20] shrink-0">{Math.round(v * 100)}{u}</span>
        </div>
      ))}
      <div className="text-[10px] text-zinc-500 pt-2">Recorded {dt}</div>
    </div>
  );
}

/* ─── Forms ───────────────────────────────────────────────────────── */
export function FormsWidget({ testid = "widget-forms" }) {
  return <Shell kind="forms" testid={testid}>{({ bucket, editing }) => <FormsBody bucket={bucket} editing={editing} />}</Shell>;
}
function FormsBody({ bucket, editing }) {
  const { data } = useSWR("/form-assignments/mine", (u) => api.get(u).then((r) => r.data), swrOpts);
  const list = Array.isArray(data) ? data : [];
  const outstanding = list.filter((a) => (a.status || "assigned") !== "completed");
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-heading text-3xl text-[#f47a20] leading-none">{outstanding.length}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{outstanding.length === 1 ? "form to complete" : "forms to complete"}</div>
        <CardCTA to="/app/forms" label="Open Forms" editing={editing} testid="widget-forms-open-sm" />
      </div>
    );
  }
  if (list.length === 0) return <div className="text-zinc-500 text-xs flex-1 flex items-center">No forms assigned.</div>;
  const target = outstanding[0] || list[0];
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="text-zinc-200 truncate">{target.title || target.form_title || "Assigned form"}</div>
        <div className="text-[10px] text-zinc-500 mt-1">Status: {target.status || "assigned"}{target.due_at ? ` · due ${target.due_at.slice(0,10)}` : ""}</div>
        <div className="mt-auto"><CardCTA to="/app/forms" label="Complete form" editing={editing} testid="widget-forms-complete" primary /></div>
      </div>
    );
  }
  const limit = bucket === "1x2" ? 6 : 10;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs">
      {list.slice(0, limit).map((a) => (
        <Link key={a.id} to="/app/forms" tabIndex={editing ? -1 : 0} aria-disabled={editing}
              onClick={(e) => { if (editing) e.preventDefault(); }}
              className="py-2 border-b border-white/5 last:border-b-0 flex items-center justify-between gap-2 hover:text-[#f47a20]">
          <span className="truncate">{a.title || a.form_title || "Assigned form"}</span>
          <span className="text-[10px] text-zinc-500 shrink-0">{a.status || "assigned"}</span>
        </Link>
      ))}
    </div>
  );
}

/* ─── Videos (patient uploads) ─────────────────────────────────────── */
export function VideosWidget({ testid = "widget-videos" }) {
  return <Shell kind="videos" testid={testid}>{({ bucket, editing }) => <VideosBody bucket={bucket} editing={editing} />}</Shell>;
}
function VideosBody({ bucket, editing }) {
  const { data } = useSWR("/videos/mine", (u) => api.get(u).then((r) => r.data), swrOpts);
  const list = Array.isArray(data) ? data : [];
  const latest = list[0];
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-heading text-3xl text-[#f47a20] leading-none">{list.length}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">your uploads</div>
        <CardCTA to="/app/videos" label="Upload video" editing={editing} testid="widget-videos-upload-sm" primary />
      </div>
    );
  }
  if (!latest) {
    return (
      <div className="flex-1 flex flex-col text-xs">
        <div className="text-zinc-500 flex-1 flex items-center">No uploads yet.</div>
        <CardCTA to="/app/videos" label="Upload your first video" editing={editing} testid="widget-videos-upload-empty" primary />
      </div>
    );
  }
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex gap-3 min-h-0">
        <div className="w-24 h-full rounded bg-white/5 flex items-center justify-center shrink-0">
          <Video size={20} className="text-zinc-500" />
        </div>
        <div className="flex-1 flex flex-col text-xs min-w-0">
          <div className="text-zinc-200 truncate">{latest.title || latest.filename || "Uploaded video"}</div>
          <div className="text-[10px] text-zinc-500 mt-1">{(latest.created_at || "").slice(0,10)}{latest.review_status ? ` · ${latest.review_status}` : ""}</div>
          <div className="mt-auto"><CardCTA to="/app/videos" label="Upload video" editing={editing} testid="widget-videos-upload" /></div>
        </div>
      </div>
    );
  }
  const limit = bucket === "1x2" ? 4 : 6;
  return (
    <div className="flex-1 min-h-0 flex flex-col gap-2">
      <div className="rounded bg-white/5 h-24 flex items-center justify-center shrink-0">
        <Video size={22} className="text-zinc-500" />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto text-xs pr-1">
        {list.slice(0, limit).map((v) => (
          <Link key={v.id} to="/app/videos" tabIndex={editing ? -1 : 0} aria-disabled={editing}
                onClick={(e) => { if (editing) e.preventDefault(); }}
                className="py-1.5 border-b border-white/5 last:border-b-0 flex items-center justify-between gap-2 hover:text-[#f47a20]">
            <span className="truncate">{v.title || v.filename || (v.created_at || "").slice(0, 10)}</span>
            <span className="text-[10px] text-zinc-500 shrink-0">{v.review_status || "uploaded"}</span>
          </Link>
        ))}
      </div>
      <CardCTA to="/app/videos" label="Upload video" editing={editing} testid="widget-videos-upload-lg" primary />
    </div>
  );
}

/* ─── Notifications ────────────────────────────────────────────────── */
export function NotificationsWidget({ testid = "widget-notifications" }) {
  return <Shell kind="notifications" testid={testid}>{({ bucket, editing }) => <NotificationsBody bucket={bucket} editing={editing} />}</Shell>;
}
function NotificationsBody({ bucket, editing }) {
  const { data } = useSWR("/notifications?limit=20", (u) => api.get(u).then((r) => r.data), swrOpts);
  const notes = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
  const unread = notes.filter((n) => !n.read);
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-heading text-3xl text-[#f47a20] leading-none">{unread.length}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{unread[0]?.title || (unread.length ? "unread updates" : "you're all caught up")}</div>
        <CardCTA to="/app/messages" label="Open" editing={editing} testid="widget-notifications-open-sm" />
      </div>
    );
  }
  if (notes.length === 0) return <div className="text-zinc-500 text-xs flex-1 flex items-center">You're all caught up.</div>;
  const limit = bucket === "2x1" ? 3 : bucket === "1x2" ? 5 : 8;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs">
      {notes.slice(0, limit).map((n) => (
        <Link key={n.id} to={n.link || "/app"} tabIndex={editing ? -1 : 0} aria-disabled={editing}
              onClick={(e) => { if (editing) e.preventDefault(); }}
              className={`py-2 border-b border-white/5 last:border-b-0 flex items-start gap-2 hover:text-[#f47a20] ${n.read ? "text-zinc-500" : "text-zinc-200"}`}>
          <span className="w-1.5 h-1.5 rounded-full bg-[#f47a20] mt-1.5 shrink-0" style={{ opacity: n.read ? 0 : 1 }} />
          <div className="flex-1 min-w-0">
            <div className="truncate">{n.title || n.body || n.kind}</div>
            {(bucket === "1x2" || bucket === "2x2") && n.body && n.body !== n.title && (
              <div className="text-[10px] text-zinc-500 line-clamp-1 mt-0.5">{n.body}</div>
            )}
          </div>
          <span className="text-[9px] text-zinc-600 shrink-0">{(n.created_at || "").slice(5, 10)}</span>
        </Link>
      ))}
    </div>
  );
}

/* ─── Today's Plan ─────────────────────────────────────────────────── */
export function PlanWidget({ testid = "widget-plan" }) {
  return <Shell kind="plan" testid={testid}>{({ bucket, editing }) => <PlanBody bucket={bucket} editing={editing} />}</Shell>;
}
function PlanBody({ bucket, editing }) {
  const { data } = useSWR("/programmes/mine", (u) => api.get(u).then((r) => r.data), swrOpts);
  const p = data || null;
  const exercises = p?.exercises || [];
  const total = exercises.length;
  // Consider "today's" set as everything in the current programme — real
  // scheduling data lives in /my-plan which we don't fetch here to stay
  // within the shared programme cache.
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end min-h-0">
        <div className="text-xs text-zinc-400 truncate">{p?.title || "No programme"}</div>
        <div className="fl-heading text-3xl text-[#f47a20] leading-none mt-1">{total}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{total === 1 ? "exercise" : "exercises"}</div>
        <CardCTA to="/app/plan" label={total ? "Start session" : "View plan"} editing={editing} testid="widget-plan-start-sm" primary disabled={!total} />
      </div>
    );
  }
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="text-zinc-200 truncate">Your {p?.title || "programme"} session contains {total} {total === 1 ? "exercise" : "exercises"} today.</div>
        <div className="mt-1 text-[10px] text-zinc-500 line-clamp-2">
          {exercises.slice(0, 3).map((e) => e.name).filter(Boolean).join(" · ") || "No exercises scheduled."}
        </div>
        <div className="mt-auto flex gap-2">
          <CardCTA to="/app/plan" label="Start" editing={editing} testid="widget-plan-start" primary disabled={!total} />
        </div>
      </div>
    );
  }
  if (bucket === "1x2") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="text-zinc-200 truncate">{p?.title || "No programme"}</div>
        <div className="text-[10px] text-zinc-500 mb-2">{total} scheduled today</div>
        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          {exercises.length === 0 && <div className="text-zinc-500 text-[10px]">No exercises assigned yet.</div>}
          {exercises.slice(0, 8).map((e) => (
            <div key={e.id || e.name} className="py-1.5 border-b border-white/5 last:border-b-0">
              <div className="text-zinc-200 truncate">{e.name}</div>
              {(e.sets || e.reps) && <div className="text-[10px] text-zinc-500">{e.sets ? `${e.sets} × ` : ""}{e.reps || ""}</div>}
            </div>
          ))}
        </div>
        <div className="mt-2 shrink-0"><CardCTA to="/app/plan" label="Start session" editing={editing} testid="widget-plan-start-tall" primary disabled={!total} /></div>
      </div>
    );
  }
  // 2x2
  return (
    <div className="flex-1 flex flex-col text-xs min-h-0">
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-zinc-200 truncate">{p?.title || "No programme"}</div>
        <div className="text-[10px] text-zinc-500 shrink-0">{total} today</div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto pr-1 mt-2">
        {exercises.length === 0 && <div className="text-zinc-500">No exercises assigned yet.</div>}
        {exercises.slice(0, 12).map((e) => (
          <div key={e.id || e.name} className="py-2 border-b border-white/5 last:border-b-0 flex items-center gap-2">
            {e.thumbnail_url ? (
              <img src={e.thumbnail_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
            ) : (
              <div className="w-10 h-10 rounded bg-white/5 flex items-center justify-center shrink-0"><Dumbbell size={12} className="text-zinc-500" /></div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-zinc-200 truncate">{e.name}</div>
              {(e.sets || e.reps) && <div className="text-[10px] text-zinc-500">{e.sets ? `${e.sets} × ` : ""}{e.reps || ""}</div>}
            </div>
            <ChevronRight size={12} className="text-zinc-500 shrink-0" />
          </div>
        ))}
      </div>
      <div className="mt-2 shrink-0"><CardCTA to="/app/plan" label="Start session" editing={editing} testid="widget-plan-start-lg" primary disabled={!total} /></div>
    </div>
  );
}

/* ─── Adherence ────────────────────────────────────────────────────── */
export function AdherenceWidget({ testid = "widget-adherence" }) {
  return <Shell kind="adherence" testid={testid}>{({ bucket }) => <AdherenceBody bucket={bucket} />}</Shell>;
}
function AdherenceBody({ bucket }) {
  const { data } = useSWR("/stats/adherence", (u) => api.get(u).then((r) => r.data), swrOpts);
  const pct = data?.adherence ?? data?.percent ?? 0;
  const days = data?.days || [];
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-heading text-3xl text-[#f47a20] leading-none">{Math.round(pct)}%</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">7-day adherence</div>
        <div className="h-1.5 bg-white/10 rounded mt-2 overflow-hidden">
          <div className="h-full bg-[#f47a20]" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      </div>
    );
  }
  if (bucket === "1x2" || bucket === "2x2") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0 gap-2">
        <div className="flex items-baseline gap-2"><span className="fl-heading text-2xl text-[#f47a20] leading-none">{Math.round(pct)}%</span><span className="text-[10px] text-zinc-500">7-day</span></div>
        <div className="flex-1 min-h-0">
          {days.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={days.map((d) => ({ date: (d.date || "").slice(5), sessions: d.sessions || 0 }))} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
                <XAxis dataKey="date" stroke="#71717A" fontSize={9} />
                <YAxis stroke="#71717A" fontSize={9} width={20} allowDecimals={false} />
                <Bar dataKey="sessions" fill="#f47a20" />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-zinc-500">Log a session to see your history.</div>}
        </div>
      </div>
    );
  }
  // 2x1
  return (
    <div className="flex-1 flex flex-col text-xs min-h-0">
      <div className="flex items-baseline gap-2"><span className="fl-heading text-2xl text-[#f47a20] leading-none">{Math.round(pct)}%</span><span className="text-[10px] text-zinc-500">7-day</span></div>
      <div className="flex-1 min-h-0 mt-1">
        {days.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={days.map((d) => ({ date: (d.date || "").slice(5), sessions: d.sessions || 0 }))} margin={{ top: 4, right: 4, left: -30, bottom: 0 }}>
              <XAxis dataKey="date" stroke="#71717A" fontSize={9} />
              <YAxis stroke="#71717A" fontSize={9} width={20} allowDecimals={false} />
              <Bar dataKey="sessions" fill="#f47a20" />
            </BarChart>
          </ResponsiveContainer>
        ) : <div className="text-zinc-500">Log a session to see your history.</div>}
      </div>
    </div>
  );
}

/* ─── Progress (session count) ─────────────────────────────────────── */
export function ProgressWidget({ testid = "widget-progress" }) {
  return <Shell kind="progress" testid={testid}>{({ bucket }) => <ProgressBody bucket={bucket} />}</Shell>;
}
function ProgressBody({ bucket }) {
  const { data } = useSWR("/stats/adherence", (u) => api.get(u).then((r) => r.data), swrOpts);
  const days = data?.days || [];
  const pct = data?.adherence ?? data?.percent ?? null;
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-heading text-3xl text-[#f47a20] leading-none">{pct != null ? `${Math.round(pct)}%` : "—"}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">7-day completion</div>
      </div>
    );
  }
  if (days.length === 0) return <div className="text-zinc-500 text-xs flex-1 flex items-center">Log a session to see progress.</div>;
  const chartData = days.map((d) => ({ date: (d.date || "").slice(5), sessions: d.sessions || 0 }));
  return (
    <div className="flex-1 min-h-0">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid stroke="#222" strokeDasharray="3 3" />
          <XAxis dataKey="date" stroke="#71717A" fontSize={10} />
          <YAxis stroke="#71717A" fontSize={10} allowDecimals={false} width={26} />
          <Tooltip contentStyle={{ background: "#111", border: "1px solid #333", fontSize: 11 }} />
          <Line type="monotone" dataKey="sessions" stroke="#f47a20" strokeWidth={2} dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ─── Appointments / Booking ───────────────────────────────────────── */
export function AppointmentsWidget({ kind = "appointments", testid = "widget-appointments" }) {
  return <Shell kind={kind} testid={testid}>{({ bucket, editing }) => <AppointmentsBody bucket={bucket} editing={editing} />}</Shell>;
}
function AppointmentsBody({ bucket, editing }) {
  const { data } = useSWR("/appointments/mine", (u) => api.get(u).then((r) => r.data), swrOpts);
  const list = Array.isArray(data) ? data : [];
  const upcoming = list.filter((a) => new Date(a.scheduled_at) > new Date()).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const next = upcoming[0] || null;
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        {next ? (
          <>
            <div className="text-xs text-zinc-400 truncate">Next visit</div>
            <div className="fl-heading text-lg text-[#f47a20] leading-tight">{new Date(next.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</div>
            <div className="text-[10px] text-zinc-500">{new Date(next.scheduled_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
          </>
        ) : <div className="text-xs text-zinc-500">No appointment yet</div>}
        <CardCTA to="/app/appointments" label={next ? "Manage" : "Book now"} editing={editing} testid="widget-appointments-cta-sm" primary />
      </div>
    );
  }
  if (bucket === "2x1") {
    if (!next) return (
      <div className="flex-1 flex flex-col text-xs">
        <div className="text-zinc-500 flex-1 flex items-center">No upcoming appointments.</div>
        <CardCTA to="/app/appointments" label="Book an appointment" editing={editing} testid="widget-appointments-book" primary />
      </div>
    );
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="text-zinc-200 truncate">{next.service}</div>
        <div className="text-[10px] text-zinc-500 mt-1">
          {new Date(next.scheduled_at).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · {new Date(next.scheduled_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
        </div>
        <div className="text-[10px] text-zinc-500">{next.mode?.replace("_", " ") || "in-person"}{next.clinician_name ? ` · ${next.clinician_name}` : ""}</div>
        <div className="mt-auto"><CardCTA to="/app/appointments" label="Manage appointment" editing={editing} testid="widget-appointments-manage" /></div>
      </div>
    );
  }
  const limit = bucket === "1x2" ? 4 : 6;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs">
      {upcoming.length === 0 && (
        <div className="text-zinc-500 py-4">No upcoming appointments. <CardCTA to="/app/appointments" label="Book now" editing={editing} testid="widget-appointments-book-lg" /></div>
      )}
      {upcoming.slice(0, limit).map((a) => (
        <Link key={a.id} to="/app/appointments" tabIndex={editing ? -1 : 0} aria-disabled={editing}
              onClick={(e) => { if (editing) e.preventDefault(); }}
              className="py-2 border-b border-white/5 last:border-b-0 flex items-center justify-between gap-2 hover:text-[#f47a20]">
          <div className="min-w-0">
            <div className="text-zinc-200 truncate">{a.service}</div>
            <div className="text-[10px] text-zinc-500">
              {new Date(a.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })} · {new Date(a.scheduled_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
          <span className="text-[10px] text-zinc-500 shrink-0">{a.mode?.replace("_", " ") || "in-person"}</span>
        </Link>
      ))}
    </div>
  );
}
export function BookingWidget({ testid = "widget-booking" }) {
  return <AppointmentsWidget kind="booking" testid={testid} />;
}

/* ─── Goal & Rewards ───────────────────────────────────────────────── */
export function GoalWidget({ testid = "widget-goal" }) {
  return <Shell kind="goal" testid={testid}>{({ bucket, editing }) => <GoalBody bucket={bucket} editing={editing} />}</Shell>;
}
function GoalBody({ bucket, editing }) {
  const { data } = useSWR("/me/goal", (u) => api.get(u).then((r) => r.data), swrOpts);
  const goal = data?.goal || null;
  const days = goal?.target_date ? Math.ceil((new Date(goal.target_date + "T00:00:00").getTime() - Date.now()) / 86400000) : null;
  if (!goal) {
    return (
      <div className="flex-1 flex flex-col text-xs">
        <div className="text-zinc-300 flex-1 flex items-center">Set a personal goal to see the countdown.</div>
        <CardCTA to="/app/plan" label="Set goal" editing={editing} testid="widget-goal-set" primary />
      </div>
    );
  }
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-xs text-zinc-400 truncate">{goal.name}</div>
        <div className="fl-heading text-3xl text-[#f47a20] leading-none mt-1">{days > 0 ? days : days === 0 ? "0" : "!"}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{days > 0 ? "days to go" : days === 0 ? "today" : "past target"}</div>
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col text-xs">
      <div className="text-zinc-200 fl-heading text-lg leading-tight truncate">{goal.name}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="fl-heading text-3xl text-[#f47a20] leading-none">{days > 0 ? days : days === 0 ? "0" : "!"}</span>
        <span className="text-[10px] text-zinc-500">{days > 0 ? "days to go" : days === 0 ? "today" : "past target"}</span>
      </div>
      <div className="text-[10px] text-zinc-500 mt-2">Target: {new Date(goal.target_date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</div>
      <div className="mt-auto"><CardCTA to="/app/plan" label="Edit goal" editing={editing} testid="widget-goal-edit" /></div>
    </div>
  );
}
export function RewardsWidget({ testid = "widget-rewards" }) {
  return <Shell kind="rewards" testid={testid}>{({ bucket, editing }) => <RewardsBody bucket={bucket} editing={editing} />}</Shell>;
}
function RewardsBody({ bucket, editing }) {
  const { data } = useSWR("/me/rewards", (u) => api.get(u).then((r) => r.data), swrOpts);
  const r = data || null;
  if (!r) return <div className="text-zinc-500 text-xs flex-1 flex items-center">Rewards will unlock as your streak grows.</div>;
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="fl-heading text-3xl text-[#f47a20] leading-none">{r.current_streak}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">day completion streak</div>
        {r.next_tier && (
          <div className="h-1.5 bg-white/10 rounded mt-2 overflow-hidden">
            <div className="h-full bg-[#f47a20]" style={{ width: `${Math.min(100, Math.round((r.current_streak / (r.next_tier.threshold || 1)) * 100))}%` }} />
          </div>
        )}
      </div>
    );
  }
  const tiers = r.rewards || [];
  return (
    <div className="flex-1 flex flex-col text-xs min-h-0">
      <div className="flex items-baseline gap-2"><span className="fl-heading text-2xl text-[#f47a20] leading-none">{r.current_streak}</span><span className="text-[10px] text-zinc-500">day streak</span></div>
      {r.next_tier ? (
        <div className="text-[10px] text-zinc-400 mt-1">{r.days_until_next} more day{r.days_until_next === 1 ? "" : "s"} → <b className="text-white capitalize">{r.next_tier.tier}</b> ({r.next_tier.discount_pct}% massage)</div>
      ) : <div className="text-[10px] text-emerald-400 mt-1">All tiers unlocked.</div>}
      {(bucket === "1x2" || bucket === "2x2") && tiers.length > 0 && (
        <div className="mt-2 flex-1 min-h-0 overflow-y-auto pr-1 space-y-1.5">
          {tiers.map((t) => (
            <div key={t.tier} className="flex items-center justify-between gap-2 fl-card px-2 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <Gift size={12} className="text-[#f47a20] shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] font-semibold uppercase" style={{ color: t.tier === "gold" ? "#F5B942" : t.tier === "silver" ? "#C0C0C0" : "#CD7F32" }}>{t.tier}</div>
                  <div className="text-[9px] text-zinc-500 truncate">{t.label}</div>
                </div>
              </div>
              <code className="text-[10px] px-1.5 py-0.5 rounded bg-black/40 text-white font-mono shrink-0">{t.code}</code>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Membership ───────────────────────────────────────────────────── */
export function MembershipWidget({ testid = "widget-membership" }) {
  return <Shell kind="membership" testid={testid}>{({ bucket, editing }) => <MembershipBody bucket={bucket} editing={editing} />}</Shell>;
}
function MembershipBody({ bucket, editing }) {
  // Patient-facing entitlements live on the user document. /auth/me returns
  // the whole record including `entitlement.membership` when populated by
  // an admin. No dedicated /me/entitlements endpoint exists.
  const { data: me } = useSWR("/auth/me", (u) => api.get(u).then((r) => r.data).catch(() => null), swrOpts);
  const m = me?.entitlement?.membership || null;
  const packageName = m?.package_name || m?.plan_name || null;
  const renewsAt = m?.current_period_end || m?.renewal_at || null;

  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-xs text-zinc-400 truncate">{packageName || "No membership"}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{m?.status || (packageName ? "active" : "—")}</div>
        <CardCTA to="/app/packages" label={packageName ? "Manage" : "View plans"} editing={editing} testid="widget-membership-cta-sm" primary />
      </div>
    );
  }
  if (!packageName) {
    return (
      <div className="flex-1 flex flex-col text-xs">
        <div className="text-zinc-300 flex-1 flex items-center">You're not on a membership yet.</div>
        <CardCTA to="/app/packages" label="View plans" editing={editing} testid="widget-membership-view-plans" primary />
      </div>
    );
  }
  const benefits = m?.benefits || m?.included || [];
  const remaining = m?.credits_remaining ?? m?.remaining_credits ?? null;
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="text-zinc-200 fl-heading text-base truncate">{packageName}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">Status: {m?.status || "active"}{renewsAt ? ` · renews ${renewsAt.slice(0,10)}` : ""}</div>
        {remaining != null && <div className="text-[10px] text-zinc-400 mt-1">{remaining} sessions remaining</div>}
        <div className="mt-auto"><CardCTA to="/app/packages" label="Manage" editing={editing} testid="widget-membership-manage" /></div>
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col text-xs min-h-0">
      <div className="text-zinc-200 fl-heading text-base truncate">{packageName}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">Status: {m?.status || "active"}{renewsAt ? ` · renews ${renewsAt.slice(0,10)}` : ""}</div>
      {remaining != null && <div className="text-[10px] text-zinc-400 mt-1">{remaining} sessions remaining</div>}
      {benefits.length > 0 && (
        <div className="mt-2 flex-1 min-h-0 overflow-y-auto pr-1">
          {benefits.slice(0, 6).map((b, i) => (
            <div key={i} className="flex items-center gap-1.5 py-1 text-[10px] text-zinc-300">
              <CheckCircle2 size={10} className="text-emerald-400 shrink-0" /><span className="truncate">{typeof b === "string" ? b : b.label}</span>
            </div>
          ))}
        </div>
      )}
      <div className="mt-2 flex gap-2 shrink-0">
        <CardCTA to="/app/packages" label="Manage" editing={editing} testid="widget-membership-manage-lg" primary />
      </div>
    </div>
  );
}

/* ─── Community ────────────────────────────────────────────────────── */
export function CommunityWidget({ testid = "widget-community" }) {
  return <Shell kind="community" testid={testid}>{({ bucket, editing }) => <CommunityBody bucket={bucket} editing={editing} />}</Shell>;
}
function CommunityBody({ bucket, editing }) {
  const { data } = useSWR("/community/posts?limit=6", (u) => api.get(u).then((r) => r.data).catch(() => []), swrOpts);
  const posts = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-xs text-zinc-400 truncate">Community</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{posts.length ? `${posts.length} recent` : "explore"}</div>
        <CardCTA to="/app/community" label="Open" editing={editing} testid="widget-community-open-sm" primary />
      </div>
    );
  }
  if (posts.length === 0) return <div className="text-zinc-500 text-xs flex-1 flex items-center">No community posts yet.</div>;
  const first = posts[0];
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="text-zinc-200 fl-heading text-sm truncate">{first.title || first.body?.slice(0, 60) || "Community post"}</div>
        <div className="text-[10px] text-zinc-500 line-clamp-2 mt-1">{first.body || first.excerpt || ""}</div>
        <div className="mt-auto"><CardCTA to="/app/community" label="Open community" editing={editing} testid="widget-community-open" /></div>
      </div>
    );
  }
  const limit = bucket === "1x2" ? 4 : 6;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs">
      {posts.slice(0, limit).map((p) => (
        <Link key={p.id} to="/app/community" tabIndex={editing ? -1 : 0} aria-disabled={editing}
              onClick={(e) => { if (editing) e.preventDefault(); }}
              className="py-2 border-b border-white/5 last:border-b-0 hover:text-[#f47a20] block">
          <div className="text-zinc-200 truncate">{p.title || p.body?.slice(0, 80) || "Post"}</div>
          <div className="text-[10px] text-zinc-500 truncate mt-0.5">{p.author_name || "member"}{p.created_at ? ` · ${p.created_at.slice(0,10)}` : ""}</div>
        </Link>
      ))}
    </div>
  );
}

/* ─── Learn ────────────────────────────────────────────────────────── */
export function LearnWidget({ testid = "widget-learn" }) {
  return <Shell kind="learn" testid={testid}>{({ bucket, editing }) => <LearnBody bucket={bucket} editing={editing} />}</Shell>;
}
function LearnBody({ bucket, editing }) {
  const { data } = useSWR("/public/cms/resources?limit=6", (u) => api.get(u).then((r) => r.data).catch(() => []), swrOpts);
  const list = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-xs text-zinc-400 truncate">{list[0]?.title || "Learn"}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{list.length} resources</div>
        <CardCTA to="/app/learn" label="Open" editing={editing} testid="widget-learn-open-sm" primary />
      </div>
    );
  }
  if (list.length === 0) return <div className="text-zinc-500 text-xs flex-1 flex items-center">Resources will appear here.</div>;
  const limit = bucket === "2x1" ? 2 : bucket === "1x2" ? 4 : 6;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs">
      {list.slice(0, limit).map((r) => (
        <Link key={r.id || r.slug} to="/app/learn" tabIndex={editing ? -1 : 0} aria-disabled={editing}
              onClick={(e) => { if (editing) e.preventDefault(); }}
              className="py-2 border-b border-white/5 last:border-b-0 hover:text-[#f47a20] block">
          <div className="text-zinc-200 truncate">{r.title || "Resource"}</div>
          {r.summary && <div className="text-[10px] text-zinc-500 line-clamp-1 mt-0.5">{r.summary}</div>}
        </Link>
      ))}
    </div>
  );
}

/* ─── Wearables ───────────────────────────────────────────────────── */
export function WearablesWidget({ testid = "widget-wearables" }) {
  return <Shell kind="wearables" testid={testid}>{({ bucket, editing }) => <WearablesBody bucket={bucket} editing={editing} />}</Shell>;
}
function WearablesBody({ bucket, editing }) {
  const { data } = useSWR("/device-readings/mine", (u) => api.get(u).then((r) => r.data).catch(() => []), swrOpts);
  const list = Array.isArray(data) ? data : [];
  const connected = list.length > 0;
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-xs text-zinc-400 truncate">{connected ? "Connected" : "Not connected"}</div>
        <div className="text-[10px] text-zinc-500 mt-0.5">{connected ? `${list.length} readings` : "no device linked"}</div>
        <CardCTA to="/app/wearables" label={connected ? "Open" : "Connect"} editing={editing} testid="widget-wearables-cta-sm" primary />
      </div>
    );
  }
  if (!connected) {
    return (
      <div className="flex-1 flex flex-col text-xs">
        <div className="text-zinc-500 flex-1 flex items-center">No device connected.</div>
        <CardCTA to="/app/wearables" label="Connect a device" editing={editing} testid="widget-wearables-connect" primary />
      </div>
    );
  }
  const latest = list[0];
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs min-h-0">
        <div className="text-zinc-200 truncate">{latest.provider || "Wearable"}</div>
        <div className="text-[10px] text-zinc-500 mt-1">Last sync: {(latest.created_at || "").slice(0,16).replace("T"," ")}</div>
        <div className="mt-auto"><CardCTA to="/app/wearables" label="Manage" editing={editing} testid="widget-wearables-manage" /></div>
      </div>
    );
  }
  const limit = bucket === "1x2" ? 4 : 6;
  return (
    <div className="flex-1 min-h-0 overflow-y-auto pr-1 text-xs">
      {list.slice(0, limit).map((d) => (
        <div key={d.id} className="py-2 border-b border-white/5 last:border-b-0">
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-zinc-200 truncate">{d.metric || d.type || "reading"}</span>
            <span className="text-[#f47a20] shrink-0">{d.value != null ? `${d.value}${d.unit || ""}` : "—"}</span>
          </div>
          <div className="text-[10px] text-zinc-500">{(d.created_at || "").slice(0,10)}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Security ────────────────────────────────────────────────────── */
export function SecurityWidget({ testid = "widget-security" }) {
  return <Shell kind="security" testid={testid}>{({ bucket, editing }) => <SecurityBody bucket={bucket} editing={editing} />}</Shell>;
}
function SecurityBody({ bucket, editing }) {
  const links = [
    ["Password",        "/security-settings#password"],
    ["Two-factor auth", "/security-settings#mfa"],
    ["Active sessions", "/security-settings#sessions"],
    ["Recovery codes",  "/security-settings#recovery"],
  ];
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-xs text-zinc-400">Account security</div>
        <CardCTA to="/security-settings" label="Open Security" editing={editing} testid="widget-security-open-sm" primary />
      </div>
    );
  }
  if (bucket === "2x1") {
    return (
      <div className="flex-1 flex flex-col text-xs">
        <div className="text-zinc-300">Manage password, sessions and two-factor authentication.</div>
        <div className="mt-auto"><CardCTA to="/security-settings" label="Open Security" editing={editing} testid="widget-security-open" primary /></div>
      </div>
    );
  }
  return (
    <div className="flex-1 min-h-0 flex flex-col text-xs gap-1">
      {links.map(([label, to]) => (
        <Link key={label} to={to} tabIndex={editing ? -1 : 0} aria-disabled={editing}
              onClick={(e) => { if (editing) e.preventDefault(); }}
              className="py-2 border-b border-white/5 last:border-b-0 flex items-center justify-between gap-2 hover:text-[#f47a20]">
          <span>{label}</span><ChevronRight size={12} className="text-zinc-500" />
        </Link>
      ))}
    </div>
  );
}

/* ─── Check-In shortcut ───────────────────────────────────────────── */
export function CheckinWidget({ testid = "widget-checkin" }) {
  return <Shell kind="checkin" testid={testid}>{({ bucket, editing }) => <CheckinBody bucket={bucket} editing={editing} />}</Shell>;
}
function CheckinBody({ bucket, editing }) {
  const { data } = useSWR("/checkins/today", (u) => api.get(u).then((r) => r.data).catch(() => null), swrOpts);
  const doneToday = !!data?.done_today;
  const nextAllowed = data?.next_allowed_at ? new Date(data.next_allowed_at) : null;
  const nextLabel = nextAllowed && nextAllowed.getTime() > Date.now()
    ? `unlocks ${nextAllowed.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
    : null;
  if (bucket === "1x1") {
    return (
      <div className="flex-1 flex flex-col justify-end">
        <div className="text-xs text-zinc-400">{doneToday ? "Completed today" : "Not yet today"}</div>
        {nextLabel && <div className="text-[10px] text-zinc-500 mt-0.5">{nextLabel}</div>}
        <CardCTA to="/app/coach" label="Open Check-In" editing={editing} testid="widget-checkin-open-sm" primary />
      </div>
    );
  }
  return (
    <div className="flex-1 flex flex-col text-xs">
      <div className="text-zinc-200 fl-heading text-base">{doneToday ? "Today's check-in complete" : "Log how you're feeling"}</div>
      {data?.last_submitted_at && <div className="text-[10px] text-zinc-500 mt-1">Last: {new Date(data.last_submitted_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</div>}
      {nextLabel && <div className="text-[10px] text-zinc-500 mt-1">Next check-in {nextLabel}</div>}
      <div className="mt-auto"><CardCTA to="/app/coach" label="Open detailed Check-In" editing={editing} testid="widget-checkin-open" primary /></div>
    </div>
  );
}

/* ─── Route each kind to its widget ────────────────────────────────── */
export default function AdaptiveWidget({ kind, testid }) {
  const id = testid || `widget-${kind}`;
  switch (kind) {
    case "messages":      return <MessagesWidget testid={id} />;
    case "msg_clinician": return <MsgClinicianWidget testid={id} />;
    case "wellness":      return <PhysicalWellnessWidget testid={id} />;
    case "tests":         return <PhysicalTestsWidget testid={id} />;
    case "forms":         return <FormsWidget testid={id} />;
    case "videos":        return <VideosWidget testid={id} />;
    case "progress":      return <ProgressWidget testid={id} />;
    case "adherence":     return <AdherenceWidget testid={id} />;
    case "notifications": return <NotificationsWidget testid={id} />;
    case "plan":          return <PlanWidget testid={id} />;
    case "appointments":  return <AppointmentsWidget testid={id} />;
    case "booking":       return <BookingWidget testid={id} />;
    case "goal":          return <GoalWidget testid={id} />;
    case "rewards":       return <RewardsWidget testid={id} />;
    case "membership":    return <MembershipWidget testid={id} />;
    case "community":     return <CommunityWidget testid={id} />;
    case "learn":         return <LearnWidget testid={id} />;
    case "wearables":     return <WearablesWidget testid={id} />;
    case "security":      return <SecurityWidget testid={id} />;
    case "checkin":       return <CheckinWidget testid={id} />;
    default:              return null;
  }
}
